/**
 * Offline Storage Service
 * Manages caching of exchange rates for offline functionality
 */

interface CachedRateData {
    rates: { [key: string]: number };
    baseCurrency: string;
    timestamp: number;
    lastUpdated: string;
    source: string;
}

const STORAGE_KEYS = {
    EXCHANGE_RATES: 'p2p_exchange_rates',
    LAST_SYNC: 'p2p_last_sync',
    OFFLINE_MODE: 'p2p_offline_mode'
};

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

class OfflineStorageService {
    /**
     * Save exchange rates to localStorage
     */
    static saveExchangeRates(
        baseCurrency: string,
        rates: { [key: string]: number },
        lastUpdated: string,
        source: string
    ): void {
        try {
            const data: CachedRateData = {
                rates,
                baseCurrency,
                timestamp: Date.now(),
                lastUpdated,
                source
            };
            
            localStorage.setItem(STORAGE_KEYS.EXCHANGE_RATES, JSON.stringify(data));
            localStorage.setItem(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
            
            console.log('💾 Exchange rates saved to offline storage:', {
                baseCurrency,
                rateCount: Object.keys(rates).length,
                timestamp: new Date(data.timestamp).toLocaleString()
            });
        } catch (error) {
            console.error('❌ Failed to save exchange rates to offline storage:', error);
        }
    }

    /**
     * Get cached exchange rates from localStorage
     */
    static getCachedExchangeRates(): CachedRateData | null {
        try {
            const data = localStorage.getItem(STORAGE_KEYS.EXCHANGE_RATES);
            if (!data) {
                console.log('📭 No cached exchange rates found');
                return null;
            }

            const cachedData: CachedRateData = JSON.parse(data);
            
            // Check if cache is still valid
            const age = Date.now() - cachedData.timestamp;
            if (age > CACHE_DURATION) {
                console.log('⏰ Cached exchange rates expired (age: ' + Math.round(age / 1000 / 60) + ' minutes)');
                return null;
            }

            console.log('✅ Retrieved cached exchange rates:', {
                baseCurrency: cachedData.baseCurrency,
                rateCount: Object.keys(cachedData.rates).length,
                age: Math.round(age / 1000 / 60) + ' minutes old'
            });

            return cachedData;
        } catch (error) {
            console.error('❌ Failed to retrieve cached exchange rates:', error);
            return null;
        }
    }

    /**
     * Check if cached rates are available and valid
     */
    static hasCachedRates(): boolean {
        const cached = this.getCachedExchangeRates();
        return cached !== null;
    }

    /**
     * Get the age of cached rates in milliseconds
     */
    static getCacheAge(): number | null {
        try {
            const data = localStorage.getItem(STORAGE_KEYS.EXCHANGE_RATES);
            if (!data) return null;

            const cachedData: CachedRateData = JSON.parse(data);
            return Date.now() - cachedData.timestamp;
        } catch (error) {
            return null;
        }
    }

    /**
     * Clear all cached data
     */
    static clearCache(): void {
        try {
            localStorage.removeItem(STORAGE_KEYS.EXCHANGE_RATES);
            localStorage.removeItem(STORAGE_KEYS.LAST_SYNC);
            console.log('🗑️ Offline cache cleared');
        } catch (error) {
            console.error('❌ Failed to clear cache:', error);
        }
    }

    /**
     * Set offline mode flag
     */
    static setOfflineMode(isOffline: boolean): void {
        try {
            localStorage.setItem(STORAGE_KEYS.OFFLINE_MODE, isOffline.toString());
        } catch (error) {
            console.error('❌ Failed to set offline mode:', error);
        }
    }

    /**
     * Check if app is in offline mode
     */
    static isOfflineMode(): boolean {
        try {
            const mode = localStorage.getItem(STORAGE_KEYS.OFFLINE_MODE);
            return mode === 'true';
        } catch (error) {
            return false;
        }
    }

    /**
     * Get last sync time
     */
    static getLastSyncTime(): Date | null {
        try {
            const lastSync = localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
            return lastSync ? new Date(lastSync) : null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Check if device is online
     */
    static isOnline(): boolean {
        return navigator.onLine;
    }

    /**
     * Get storage info for debugging
     */
    static getStorageInfo(): {
        hasCachedRates: boolean;
        cacheAge: number | null;
        lastSync: Date | null;
        isOffline: boolean;
        isOnline: boolean;
    } {
        return {
            hasCachedRates: this.hasCachedRates(),
            cacheAge: this.getCacheAge(),
            lastSync: this.getLastSyncTime(),
            isOffline: this.isOfflineMode(),
            isOnline: this.isOnline()
        };
    }
}

export default OfflineStorageService;
